// Single source of truth for approved Conditions.
// Consumed by /conditions gallery, homepage Focus Areas grid,
// Journey chip mapping and any future approved-Condition-card surface.
// The visible order below IS the approved gallery order.
// Do not add, remove, rename, reorder or duplicate entries here without
// explicit written approval from Dr Tania.

export const APPROVED_CONDITIONS = [
  {
    slug: "recurrent-pregnancy-loss",
    route: "/recurrent-pregnancy-loss",
    testid: "chub-recurrent-pregnancy-loss",
    category: "Pregnancy Loss",
    title: "Recurrent Pregnancy Loss",
    cardDescription:
      "A dedicated, evidence-based guide to the evaluation and next-pregnancy care of women who have experienced two or more pregnancy losses.",
  },
  {
    slug: "rh-negative-pregnancy-high-ict-titres",
    route: "/rh-negative-pregnancy-high-ict-titres",
    testid: "chub-rh-negative-pregnancy-high-ict-titres",
    category: "Immunological",
    title: "Rh-Negative Pregnancy with High ICT Titres",
    cardDescription:
      "Specialist care for Rh-negative pregnancy with high or rising ICT titres — antibody identification, fetal RhD testing, MCA Doppler and delivery planning.",
  },
  {
    slug: "high-bile-acids-pregnancy-ihcp",
    route: "/high-bile-acids-pregnancy-ihcp",
    testid: "chub-high-bile-acids-pregnancy-ihcp",
    category: "Hepatic",
    title: "High Bile Acids in Pregnancy (IHCP)",
    cardDescription:
      "Specialist care for high bile acids and intrahepatic cholestasis of pregnancy — nutrition-led management, repeat testing, fetal-risk assessment and personalised timing of birth.",
  },
  {
    slug: "pregnancy-with-diabetes",
    route: "/pregnancy-with-diabetes",
    testid: "chub-pregnancy-with-diabetes",
    category: "Metabolic",
    title: "Pregnancy with Diabetes",
    cardDescription:
      "Specialist pregnancy care for type 1, type 2 and gestational diabetes — nutrition-led management, glucose monitoring and personalised maternal–fetal planning.",
  },
  {
    slug: "isolated-oligohydramnios-pregnancy",
    route: "/isolated-oligohydramnios-pregnancy",
    testid: "chub-isolated-oligohydramnios-pregnancy",
    category: "Placental & Fetal",
    title: "Isolated Oligohydramnios in Pregnancy",
    cardDescription:
      "Specialist assessment of isolated oligohydramnios and low amniotic fluid — confirmation of the diagnosis, fetal growth review, surveillance and personalised birth planning.",
  },
  {
    slug: "sle-in-pregnancy",
    route: "/sle-in-pregnancy",
    testid: "chub-sle-in-pregnancy",
    category: "Autoimmune",
    title: "Systemic Lupus Erythematosus (SLE) in Pregnancy",
    cardDescription:
      "Specialist pregnancy care for systemic lupus erythematosus — disease activity, lupus nephritis, hydroxychloroquine, antiphospholipid antibodies, anti-Ro/SSA and anti-La/SSB antibodies, fetal surveillance and birth planning.",
  },
  {
    slug: "coeliac-disease-in-pregnancy",
    route: "/coeliac-disease-in-pregnancy",
    testid: "chub-coeliac-disease-in-pregnancy",
    category: "Autoimmune",
    title: "Coeliac Disease in Pregnancy",
    cardDescription:
      "Specialist pregnancy care for coeliac or celiac disease — diagnosis before a gluten-free diet, micronutrient assessment, anaemia, autoimmune conditions, fetal growth and nutrition-led management.",
  },
  {
    slug: "multiple-large-uterine-fibroids-pregnancy",
    route: "/multiple-large-uterine-fibroids-pregnancy",
    testid: "chub-multiple-large-uterine-fibroids-pregnancy",
    category: "Structural",
    title: "Multiple and Large Uterine Fibroids in Pregnancy",
    cardDescription:
      "Specialist pregnancy care for multiple or large uterine fibroids, including ultrasound mapping, degeneration pain, placental relationship, fetal growth, malpresentation, birth planning and haemorrhage preparedness.",
  },
  {
    slug: "previous-dvt-pregnancy",
    route: "/previous-dvt-pregnancy",
    testid: "chub-previous-dvt-pregnancy",
    category: "Haematological",
    title: "Previous DVT and Pregnancy",
    cardDescription:
      "Specialist pregnancy care after a previous DVT or pulmonary embolism — recurrence-risk assessment, anticoagulation planning, delivery and postpartum care.",
  },
  {
    slug: "high-blood-pressure-preeclampsia-pregnancy",
    route: "/high-blood-pressure-preeclampsia-pregnancy",
    testid: "chub-high-blood-pressure-preeclampsia-pregnancy",
    category: "Hypertensive",
    title: "High Blood Pressure and Preeclampsia in Pregnancy",
    cardDescription:
      "Specialist pregnancy care for chronic hypertension, gestational hypertension and preeclampsia — diagnosis, maternal-fetal surveillance, treatment and personalised timing of birth.",
  },
];

// Fast lookup for chip / link translation.
export const APPROVED_CONDITION_ROUTE_BY_SLUG = APPROVED_CONDITIONS.reduce(
  (acc, c) => {
    acc[c.slug] = { route: c.route, title: c.title };
    return acc;
  },
  {}
);

// Legacy DB slugs that must be REPLACED (not removed) with an approved
// equivalent when they appear in existing content (Journey chips, etc.).
// Legacy slugs not listed here are fully unapproved and must be dropped.
export const LEGACY_TO_APPROVED_SLUG = {
  // "Gestational Diabetes Mellitus" legacy record → approved "Pregnancy with Diabetes"
  "gestational-diabetes": "pregnancy-with-diabetes",
};

// Resolve any incoming legacy or current slug to an approved
// { route, title } tuple, or `null` if the slug is unapproved.
export function resolveApprovedCondition(slug) {
  if (!slug) return null;
  const mapped = LEGACY_TO_APPROVED_SLUG[slug] || slug;
  return APPROVED_CONDITION_ROUTE_BY_SLUG[mapped] || null;
}
